import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Megaphone, FolderKanban,
  CreditCard, Users, LogOut, Settings as SettingsIcon, Menu, X, UserCircle2, Video, Tags,
  Target, Landmark, ClipboardList, Wallet, BookOpen } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useNotify } from "../lib/notify";
import { Button } from "./ui/button";
import api from "../lib/api";

const NAV = [
  { key: "dashboard", label: "Dashboard Real-time", short: "Dashboard", icon: LayoutDashboard, roles: ["admin", "advertiser", "cs", "finance"], path: "/dashboard" },
  { key: "adv", label: "Laporan Advertiser", short: "Advertiser", icon: Megaphone, roles: ["admin", "advertiser"], path: "/laporan-advertiser" },
  { key: "donasi", label: "Data Donasi", short: "Data Donasi", icon: ClipboardList, roles: ["admin", "cs", "finance", "advertiser"], path: "/data-donasi" },
  { key: "finance", label: "Finance", short: "Finance", icon: Wallet, roles: ["admin", "finance"], path: "/finance" },
  { key: "bukukas", label: "Buku Kas", short: "Buku Kas", icon: BookOpen, roles: ["admin", "finance"], path: "/buku-kas" },
  { key: "kampanye", label: "Kampanye", short: "Kampanye", icon: Target, roles: ["admin", "advertiser"], path: "/kampanye" },
  { key: "bank", label: "Bank Transfer", short: "Bank", icon: Landmark, roles: ["admin", "advertiser", "finance"], path: "/bank-transfer" },
  { key: "konten", label: "Konten Iklan", short: "Konten Iklan", icon: Video, roles: ["admin", "advertiser"], path: "/konten-iklan" },
  { key: "interest", label: "Kelola Interest", short: "Interest", icon: Tags, roles: ["admin", "advertiser"], path: "/kelola-interest" },
  { key: "prog", label: "Kelola Program", short: "Program", icon: FolderKanban, roles: ["admin", "advertiser"], path: "/manajemen-program" },
  { key: "akun", label: "Kelola Akun Iklan", short: "Akun Iklan", icon: CreditCard, roles: ["admin", "advertiser"], path: "/manajemen-akun-iklan" },
  { key: "user", label: "Kelola User", short: "User", icon: Users, roles: ["admin"], path: "/manajemen-user" },
  { key: "profil", label: "Profil Saya", short: "Profil", icon: UserCircle2, roles: ["admin", "advertiser", "cs", "finance"], path: "/profil" },
  { key: "settings", label: "Pengaturan System", short: "Pengaturan", icon: SettingsIcon, roles: ["admin"], path: "/pengaturan" },
];

const roleBadge = (role) => ({
  admin: "bg-purple-100 text-purple-800 border-purple-300",
  advertiser: "bg-blue-100 text-blue-800 border-blue-300",
  cs: "bg-amber-100 text-amber-800 border-amber-300",
  finance: "bg-teal-100 text-teal-800 border-teal-300",
})[role] || "bg-slate-100 text-slate-800 border-slate-300";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const notify = useNotify();
  const nav = useNavigate();
  const loc = useLocation();
  const [settings, setSettings] = useState({ yayasan_nama: "Laporan Digital Marketing", logo_data: "" });
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [loc.pathname]);

  useEffect(() => { setDrawer(false); }, [loc.pathname]);

  useEffect(() => {
    document.body.style.overflow = drawer ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawer]);

  const items = NAV.filter((n) => n.roles.includes(user?.role));
  const initial = (settings.yayasan_nama || "L").trim()[0]?.toUpperCase() || "L";
  const current = items.find((i) => loc.pathname.startsWith(i.path));

  const doLogout = async () => {
    const ok = await notify.confirm({
      title: "Keluar dari Sistem?",
      description: "Anda perlu login kembali untuk mengakses portal laporan.",
      confirmText: "Ya, Keluar",
    });
    if (!ok) return;
    await logout();
    nav("/login");
  };

  const Avatar = ({ size = "w-9 h-9", text = "text-sm" }) =>
    user?.avatar_data ? (
      <img src={user.avatar_data} alt="Foto profil" className={`${size} rounded-full object-cover flex-shrink-0 bg-white`} data-testid="user-avatar" />
    ) : (
      <div className={`${size} ${text} rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold uppercase flex-shrink-0`}>
        {user?.name?.[0] || "U"}
      </div>
    );

  const Brand = ({ compact }) => (
    <div className="flex items-center gap-2.5 min-w-0">
      {settings.logo_data ? (
        <img src={settings.logo_data} alt="Logo" className="w-9 h-9 rounded-lg object-cover bg-white p-0.5 flex-shrink-0" />
      ) : (
        <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center text-white font-extrabold flex-shrink-0">{initial}</div>
      )}
      <div className="min-w-0">
        <div className={`font-extrabold tracking-tight text-sm leading-tight truncate ${compact ? "text-slate-900" : "text-white"}`} data-testid="sidebar-brand">
          {settings.yayasan_nama}
        </div>
        <div className={`text-[10px] uppercase tracking-widest ${compact ? "text-slate-500" : "text-slate-400"}`}>Laporan DM</div>
      </div>
    </div>
  );

  const NavItems = () => (
    <nav className="mt-4 px-2 space-y-1">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.key}
            to={it.path}
            data-testid={`nav-${it.key}`}
            className={({ isActive }) =>
              `sidebar-item flex items-center gap-3 px-4 py-3 sm:py-2.5 rounded-lg text-sm font-medium ${isActive ? "active text-white" : "text-slate-300"}`
            }
          >
            <Icon size={18} className="flex-shrink-0" />
            <span>{it.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );

  const UserBox = () => (
    <div className="p-4 border-t border-slate-800">
      <button
        onClick={() => nav("/profil")}
        className="w-full flex items-center gap-3 mb-3 text-left rounded-lg hover:bg-slate-800 p-1.5 -m-1.5 transition"
        data-testid="btn-goto-profile"
      >
        <Avatar />
        <div className="flex-1 min-w-0">
          <div className="text-white text-sm font-semibold truncate" data-testid="user-name">{user?.name}</div>
          <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest border ${roleBadge(user?.role)}`} data-testid="user-role">
            {user?.role}
          </span>
        </div>
      </button>
      <Button
        variant="outline"
        size="sm"
        className="w-full border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white"
        onClick={doLogout}
        data-testid="btn-logout"
      >
        <LogOut size={14} className="mr-2" /> Keluar
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      {/* ===== Desktop sidebar ===== */}
      <aside
        className="hidden lg:flex w-64 bg-slate-900 text-slate-300 min-h-screen flex-col justify-between border-r border-slate-800 sticky top-0 h-screen overflow-y-auto"
        data-testid="app-sidebar"
      >
        <div>
          <div className="px-6 py-6 border-b border-slate-800"><Brand /></div>
          <NavItems />
        </div>
        <UserBox />
      </aside>

      {/* ===== Mobile top bar ===== */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm" data-testid="mobile-topbar">
        <div className="flex items-center gap-3 px-3 h-14">
          <button
            onClick={() => setDrawer(true)}
            className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center active:scale-95 transition flex-shrink-0"
            aria-label="Buka menu"
            data-testid="btn-open-menu"
          >
            <Menu size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-extrabold text-slate-900 truncate leading-tight">{current?.short || settings.yayasan_nama}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">{settings.yayasan_nama}</div>
          </div>
          <button onClick={() => nav("/profil")} aria-label="Profil saya" data-testid="btn-goto-profile-mobile">
            <Avatar />
          </button>
        </div>
      </header>

      {/* ===== Mobile drawer ===== */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm notify-fade" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 w-[82%] max-w-xs bg-slate-900 flex flex-col justify-between drawer-in shadow-2xl">
            <div className="overflow-y-auto">
              <div className="px-4 py-4 border-b border-slate-800 flex items-center justify-between gap-2">
                <Brand />
                <button
                  onClick={() => setDrawer(false)}
                  className="w-9 h-9 rounded-lg bg-slate-800 text-slate-300 flex items-center justify-center flex-shrink-0"
                  aria-label="Tutup menu"
                  data-testid="btn-close-menu"
                >
                  <X size={18} />
                </button>
              </div>
              <NavItems />
            </div>
            <UserBox />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 pb-10">{children}</main>
    </div>
  );
}
